<template>
  <v-card class="pa-6" flat>
    <v-form ref="form" v-model="valid">
      <!-- Title Section -->
      <h3 class="title mb-6">Edit Tenancy</h3>
      
      <!-- Search Section -->
      <v-row>
        <v-col cols="12" md="8">
          <div class="text-subtitle-1 black--text mb-2">Search for tenancy to edit</div>
          <Search 
            label="Search by property address"
            @selectedOID="fetchTenancy" 
            @clearItem="handleClearSearch"
            endpoint="tenancy"
            myParams="address"
          />
        </v-col>
      </v-row>

      <!-- Selected Tenancy Card -->
      <v-card v-if="selectedTenancyID && !fetchingTenancy" class="mt-4 mb-6" outlined>
        <v-card-text>
          <div class="text-subtitle-2 mb-1">Currently Editing:</div>
          <div class="text-body-1">{{ displayTenancyInfo }}</div>
          <div class="text-caption">Tenancy ID: {{ selectedTenancyID }}</div>
        </v-card-text>
      </v-card>

      <!-- Loading Progress -->
      <v-progress-linear v-if="fetchingTenancy" indeterminate color="secondary" class="mb-4"></v-progress-linear>

      <!-- Tenancy Details Form -->
      <div v-if="selectedTenancyID">
        <v-divider class="mb-6"></v-divider>
        
        <h4 class="subtitle-1 mb-4">Tenancy Details</h4>
        
        <!-- Date Fields -->
        <v-row>
          <v-col cols="12" md="6">
            <v-menu
              v-model="menu1"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedStartDate"
                  label="First Rent Due Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  :disabled="fetchingTenancy"
                  :rules="[rules.required]"
                  clearable
                  @click:clear="startDate = null"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                @input="menu1 = false"
              />
            </v-menu>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-menu
              v-model="menu2"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedEndDate"
                  label="Stop Collecting Date"
                  prepend-icon="mdi-calendar-remove"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  :disabled="fetchingTenancy"
                  clearable
                  @click:clear="endDate = null"
                  hint="Leave empty to continue collecting rent"
                  persistent-hint
                />
              </template>
              <v-date-picker
                v-model="endDate"
                @input="menu2 = false"
                :min="startDate"
              />
            </v-menu>
          </v-col>
        </v-row>
        
        <!-- Service Type and Rent -->
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="serviceType"
              :items="serviceTypeItems"
              label="Service Type"
              outlined
              dense
              return-object
              item-text="text"
              item-value="value"
              :disabled="fetchingTenancy"
              :rules="[rules.required]"
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="rent"
              label="Rent Per Calendar Month"
              outlined
              dense
              type="number"
              prefix="£"
              :disabled="fetchingTenancy"
              :rules="[rules.required, rules.positiveNumber]"
            />
          </v-col>
        </v-row>
        
        <!-- Action Buttons -->
        <v-row class="mt-6">
          <v-col cols="12" class="text-right">
            <v-btn
              text
              @click="handleClearSearch"
              :disabled="loading"
              class="mr-3"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :loading="loading"
              :disabled="!valid || fetchingTenancy"
              @click="saveTenancy"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-8">
        <v-icon size="48" color="grey lighten-2">mdi-home-search-outline</v-icon>
        <p class="text-subtitle-1 grey--text mt-3">Search for a tenancy above to start editing</p>
      </div>
    </v-form>

    <!-- Snackbar for notifications -->
    <Snackbar 
      :snackbarDialog="snackbar"
      :snackbarText="snackbarText"
      :timeout="timeout"
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
    />
  </v-card>
</template>

<script>
import { amplifyProxy } from '@/services/directApi'
import { mapGetters } from 'vuex'
import Search from '@/components/CloudSearch/Search'
import Snackbar from '@/components/Snackbars/TheSnackbar'
import moment from 'moment/src/moment'

export default {
  components: {
    Search,
    Snackbar
  },
  data() {
    return {
      valid: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      timeout: 3000,
      loading: false,
      fetchingTenancy: false,
      selectedTenancyID: null,
      startDate: '',
      endDate: '',
      serviceType: '',
      rent: '',
      menu1: false,
      menu2: false,
      tenancyData: null,
      rules: {
        required: value => !!value || 'Required field',
        positiveNumber: value => {
          if (value === null || value === '') return 'Required field'
          return value > 0 || 'Amount must be greater than 0'
        }
      }
    }
  },
  methods: {
    clearForm() {
      this.startDate = ''
      this.endDate = ''
      this.serviceType = ''
      this.rent = ''
      this.tenancyData = null
    },

    handleClearSearch() {
      this.selectedTenancyID = null
      this.clearForm()
    },

    closeSnackbar() {
      this.snackbar = false
    },

    async fetchTenancy(id) {
      this.selectedTenancyID = id
      this.clearForm()
      this.fetchingTenancy = true

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/tenancies/${id}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        const data = response.data
        this.tenancyData = data

        this.startDate = data.StartDate ? data.StartDate.substr(0, 10) : ''
        if (data.EndDate === null) {
          this.endDate = ''
        } else {
          this.endDate = data.EndDate.substr(0, 10)
        }
        // Find the service type from the store
        const serviceTypeItem = this.serviceTypeItems.find(item => item.value === data.ServiceTypeOID)
        this.serviceType = serviceTypeItem || {
          value: data.ServiceTypeOID,
          text: data.ServiceType
        }
        this.rent = data.RentPerCalendarMonth || 0
      } catch (error) {
        console.error('Error fetching tenancy:', error)
        this.snackbar = true
        this.snackbarText = 'Error fetching tenancy details: ' + (error.response?.data?.message || error.message)
        this.snackbarColor = 'red'
      } finally {
        this.fetchingTenancy = false
      }
    },

    async saveTenancy() {
      this.loading = true

      const tenancyObject = {
        StartDate: `${this.startDate}T00:00:00`,
        EndDate: `${this.endDate}T00:00:00`,
        ServiceType: this.serviceType.value,
        RentPerCalendarMonth: this.rent,
      }

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/tenancies/${this.selectedTenancyID}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        await amplifyProxy.patch(endpoint, tenancyObject, options)

        this.snackbar = true
        this.snackbarText = 'Tenancy update successful'
        this.snackbarColor = 'green'
        await this.fetchTenancy(this.selectedTenancyID)
      } catch (error) {
        this.snackbar = true
        this.snackbarText = 'ERROR: ' + (error.response?.data || error.message)
        this.snackbarColor = 'red'
        await this.fetchTenancy(this.selectedTenancyID)
      } finally {
        this.loading = false
      }
    }
  },
  computed: {
    ...mapGetters('TenanciesManagement', ['serviceTypeOptions']),
    
    serviceTypeItems() {
      return this.serviceTypeOptions || []
    },
    
    displayTenancyInfo() {
      if (!this.tenancyData) return 'No tenancy selected'
      const address = this.tenancyData.PropertyAddress || 'Unknown property'
      const tenant = this.tenancyData.HeadTenant || 'Unknown tenant'
      return `${address} - ${tenant}`
    },
    
    formattedStartDate() {
      return this.startDate ? moment(this.startDate).format('DD/MM/YYYY') : ''
    },
    formattedEndDate() {
      return this.endDate ? moment(this.endDate).format('DD/MM/YYYY') : ''
    }
  },
  async created() {
    // Fetch service types if not already loaded
    if (!this.serviceTypeOptions || this.serviceTypeOptions.length === 0) {
      await this.$store.dispatch('TenanciesManagement/fetchServiceTypes')
    }
  }
}
</script>